import { useForm, Link } from "@inertiajs/react";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Download, FileUp, Upload } from "lucide-react";

type Props = {
  templateUrl: string;
  flash?: { notice?: string; alert?: string };
};

export default function BoozeImportsNew({ templateUrl, flash }: Props) {
  useFlashToast(flash);
  const form = useForm({ file: null as File | null });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.post("/booze_imports", { forceFormData: true });
  }

  return (
    <div className="px-4 py-8">
      <div className="mx-auto max-w-xl space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/boozes">
            <ArrowLeft className="size-4" />
            Back to booze
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Upload className="size-5 text-amber-600" />
              <CardTitle>Import bottles (CSV)</CardTitle>
            </div>
            <CardDescription>
              Upload a CSV of individual bottles. Producers are matched by name
              (case-insensitive) for your organization and the country on each
              row. Rows with unknown producers or other validation issues are
              skipped; other rows are still imported.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div>
                <Button type="button" variant="outline" size="sm" asChild>
                  <a href={templateUrl} download>
                    <Download className="size-4" />
                    Download template (CSV)
                  </a>
                </Button>
                <p className="mt-2 text-sm text-muted-foreground">
                  Columns: name, description, category, country, producer,
                  size(mL), count (optional; whole number 1–10000, blank = 1;
                  creates that many identical rows)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="booze-import-file">CSV file</Label>
                <Input
                  id="booze-import-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    form.setData("file", f);
                  }}
                />
                {form.errors.file && (
                  <p className="text-sm text-destructive">{form.errors.file}</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2 border-t pt-4">
              <Button type="submit" disabled={form.processing} className="gap-2">
                <FileUp className="size-4" />
                {form.processing ? "Starting…" : "Start import"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
